import * as Yup from 'yup';

import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';

import { DateTime } from 'luxon';

import { displayLabelCustomer } from '../../utils/displayLabels';
import SelectInventoryItem from './Components/SelectInventoryItem';
import EditInventoryItem from '../InventoryItem/EditInventory';

import EditCustomer from '../Customers/EditCustomer';

import MediaZone from '../Schedule/Components/MediaZone';

import SimpleChip from '../../utils/utilities/SimpleChip';

import WigItem from '../Inventory/Components/WigItem';

import Values from '../InventoryItem/Components/Values';

const headers = [
  {
    label: "Order ID",
    id: "wig_order_num"
  },
  {
    label: "Customer",
    id: "customer_name"
  },
  {
    label: "Brand",
    id: "brand_name"
  },
  {
    label: "Rep",
    id: "stylist"
  },
  {
    label: "Appt",
    id: "appointment_start_time",
    date: true
  },
  // {
  //   label: "SKU",
  //   id: "wig_sku"
  // },
  {
    label: "Wig ID",
    id: "wig_id",
    render: row => <WigItem wig_id={row.wig_id} sku={row.wig_sku} />
  },
  {
    label: "Status",
    id: "fulfillment_status",
    multi: true,
    values: [{value: "pending", label: "Pending", status: "warning"}, {value: "cancelled", label: "Cancelled", status: "error"}, {value: "assigned", label: "Assigned", status: "success"}, {value: "fulfilled", label: "Fulfilled", status: "success"}]
  },
  {
    label: "Entered On",
    dateShort: true,
    id: "created_at"
  },
  {
    label: "Due Date",
    dateShort: true,
    id: "due_date"
  },
  {
    label: "Fulfilled On",
    dateShort: true,
    id: "date_fulfilled"
  },
  {
    label: "Sales Rep",
    id: "sales_rep",
    render: row => row.sales_rep ? <SimpleChip label={row.sales_rep} /> : "-"
  }
];

const customHeaders = [
  {
    label: "Order ID",
    id: "wig_order_num"
  },
  {
    label: "Customer",
    id: "customer_name"
  },
  {
    label: "Appt",
    id: "appointment_start_time",
    date: true
  },
  {
    label: "Wig ID",
    id: "wig_id",
  },
  {
    label: "Status",
    id: "fulfillment_status",
    multi: true,
    values: [{value: "pending", label: "Pending", status: "warning"}, {value: "cancelled", label: "Cancelled", status: "error"}, {value: "assigned", label: "Assigned", status: "success"}, {value: "fulfilled", label: "Fulfilled", status: "success"}]
  },
  {
    label: "Date Created",
    date: true,
    id: "created_at"
  }
];

const filters = [
  {
    id: "date_entered",
    type: "created_at",
    label: "Entered On",
  },
  {
    id: "date_fulfilled",
    type: "date",
    label: "Fulfilled On",
  },
  {
    id: "due_date",
    type: "date",
    label: "Due Date",
  },
  {
    id: "u.display_name",
    type: "autoMulti",
    label: "Sales Rep",
    url: '/users',
    fieldOptions: {
      fieldName: "display_name",
      displayField: "display_name"
    },
    prefetch: true
  },
  {
    id: "brand_name",
    type: "autoMulti",
    label: "Brands",
    url: '/vendors/brands',
    fieldOptions: {
      fieldName: "brand_name",
      displayField: "brand_name"
    },
    prefetch: true
  },
]

const tabs = [
  {id: "regular", label: "General", Icon: AllInclusiveRoundedIcon },
  {id: "custom", label: "Custom Orders", Icon: NoteAltOutlinedIcon }
]

const fields = [
  { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "customer_id",
    label: "Select Customer",
    url: "customers",
    fieldName: "customer_id",
    displayField: "full_name",
    formUpdates: [{name: "customer_name", field: "full_name"}, {name: "customer_phone", field: "mobile_phone"}],
    setDisplayField: "customer_name",
    displayLabel: displayLabelCustomer,
    server: true,
    FormComponent: <EditCustomer dialog={true}/>,
    size: {xs: 12, lg: 6},
  },
  { 
    fieldType: "debounceSearchSolo",
    formFieldType: "select",
    select: [],
    name: "stylist_id",
    label: "Select Stylist",
    url: "stylists",
    fieldName: "stylist_id",
    displayField: "full_name",
    formUpdates: [{name: "stylist_name", field: "full_name"}],
    setDisplayField: "stylist_name",
    server: true,
    size: {xs: 12, lg: 6},
  },
  {
    fieldType: "formField",
    formFieldType: "date",
    name: "due_date",
    label: "Due Date",
    size: {xs: 12, lg: 6},
  },
  {
    fieldType: "divider",
    header: "Order Details",
    name: "order_details",
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "debounceSearchSolo",
    name: "brand_name",
    label: "Select Wig Brand",
    url: "vendors/brands",
    fieldName: "brand_name",
    displayField: "brand_name",
    formUpdates: [{name: "vendor_id", field: "vendor_id"}],
    checkDisplayField: (values) => { return values?.order_type !== "custom"},
    size: {xs: 12, lg: 12},
  },
  {
    fieldType: "Component",
    Component: <Values />,
    name: "values",
    checkDisplayField: (values) => { return values?.order_type !== "custom"},
    size: {xs: 12, lg: 12},
  },
  // {
  //   fieldType: "Component",
  //   Component: <SelectInventoryItem FormComponent={<EditInventoryItem dialog={true} ignore_id={true} />} />,
  //   name: "select_wig",
  //   size: {xs: 12, lg: 12},
  //   checkDisplayField: (values) => { return values?.order_type !== "custom"},
  // },
  {
    fieldType: "debounceSearchSolo",
    name: "brand_name",
    label: "Select Wig Brand",
    url: "vendors/brands",
    fieldName: "brand_name",
    displayField: "brand_name",
    size: {xs: 12, lg: 12},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "color",
    label: "Color",
    required: true,
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "length",
    label: "Length",
    type: "text",
    required: true,
    size: {xs: 12, md: 2},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "select",
    name: "length_unit",
    label: "Length Unit",
    required: true,
    options: [{value: "in", label: "Inches"}, {value: "cm", label: "CM"}],
    size: {xs: 12, md: 2},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "cap_size",
    label: "Cap Size",
    type: "text",
    required: true,
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "material",
    label: "Wig Type",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "hairstyle",
    label: "Hairstyle",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "line",
    label: "Collection",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "coloring",
    label: "Additional Color Info",
    helperText: "Highlights, Ombre, etc",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "layers",
    label: "Layers",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "divider",
    header: "Price Details",
    name: "price_details",
    size: {xs: 12, lg: 12},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "cost_subtotal",
    label: "Cost",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "price",
    label: "Price",
    type: "text",
    size: {xs: 12, md: 4},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "divider",
    header: "Additional Details",
    name: "additional_details",
    size: {xs: 12, lg: 12},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "Component",
    Component: <MediaZone />,
    name: "media_zone",
    size: {xs: 12, lg: 12},
    checkDisplayField: (values) => { return values?.order_type === "custom"}
  },
  {
    fieldType: "formField",
    formFieldType: "text",
    name: "details",
    label: "Details",
    type: "text",
    multiline: true,
    rows: 4,
    size: {xs: 12},
  }
  
]

const initialValues = {
  order_type: 'regular',
  invoice_id: '', // for custom orders
  customer_id: '',
  appointment_id: '',
  inventory_item_id: '',
  stylist_id: '',
  wig_id: '',
  fulfillment_status: '',
  is_assigned: false,
  details: '',
  notes: '',
  media: [],
  due_date: DateTime.now(),
  color: '',
  brand_name: '',
  length: '',
  length_unit: 'in',
  material: '',
  cap_size: '',
  hairstyle: '',
  line: '',
  coloring: '',
  layers: '',
  price: ''
}

const additionalState = {
  customer_name: '',
  stylist_name: '',
  appointment_title: '',
  inventory_item_sku: '',
  vendor_name: '',
  customer_phone: '',
  wig_sold_on: DateTime.now(),
}

const schema = Yup.object().shape({
  // inventory_item_id: Yup.string().when('order_type', {
  //   is: 'regular',
  //   then: (schema) => schema.required('Required'),
  //   otherwise: (schema) => schema.notRequired()
  // }),
  inventory_item_id: Yup.string().nullable(),
  brand_name: Yup.string().when('order_type', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired()
  }),
  customer_id: Yup.string().required('Required'),
  appointment_id: Yup.string(),
  order_type: Yup.string().required('Required'),
  color: Yup.string().when('order_type', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired()
  }),
  length: Yup.string().when('order_type', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired()
  }),
  cap_size: Yup.string().when('order_type', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired()
  }),
  price: Yup.string().when('order_type', {
    is: 'custom',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired()
  }),
});

export { headers, filters, fields, schema, initialValues, additionalState, tabs };